export type EditorialCard = {
  _id: string;
  _type: "newsArticle" | "postArticle";
  title: string;
  slug: { current: string };
  mainImage?: any;
  publishedAt: string;
  tags?: string[];
};
export type UpcomingEditorialEvent = {
  name: string;
  slug: string;
  start_date: string;
  end_date?: string;
  image_url?: string;
  cityName?: string;
  countryName?: string;
  event_timezone?: string | null;
  automation_key?: string | null;
  organizer_name?: string;
};

/** Calendar guides expire independently of publication date. Evergreen history stays useful. */
export function expiredEventGuide(
  card: EditorialCard,
  now = Date.now(),
): boolean {
  const text = `${card.title} ${card.slug.current}`
    .toLowerCase()
    .replace(/[-_]/g, " ")
    .replace(/[’']/g, "");
  if (
    !/techno radar|where to rave|upcoming|(?:best|biggest|top|guide).{0,60}(?:festivals|raves|events)|new years eve|nye/.test(
      text,
    )
  )
    return false;
  const years = [...text.matchAll(/\b(20\d{2})\b/g)].map((match) =>
    Number(match[1]),
  );
  const year = years.length
    ? Math.max(...years)
    : new Date(card.publishedAt).getUTCFullYear();
  if (/new years eve|nye/.test(text) && years.length) {
    const eveYear = Math.min(...years);
    return now >= Date.UTC(eveYear + 1, 0, 2);
  }
  const months = [
    "january",
    "february",
    "march",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
  ];
  const monthName =
    /\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/.exec(
      text,
    )?.[1];
  const month = months.indexOf(monthName || "");
  if (month >= 0) return now >= Date.UTC(year, month + 1, 1);
  if (/\bsummer\b/.test(text)) return now >= Date.UTC(year, 8, 1);
  if (/\bspring\b/.test(text)) return now >= Date.UTC(year, 5, 1);
  if (/\bautumn\b|\bfall\b/.test(text)) return now >= Date.UTC(year, 11, 1);
  return (
    years.length > 0 &&
    (year < new Date(now).getUTCFullYear() ||
      (year === new Date(now).getUTCFullYear() &&
        now - Date.parse(card.publishedAt) > 90 * 86400000))
  );
}

export function selectUpcomingEvents(
  events: UpcomingEditorialEvent[],
  now = Date.now(),
) {
  const seen = new Set<string>(),
    organizers = new Map<string, number>();
  return events
    .filter(
      (event) =>
        Number.isFinite(Date.parse(event.start_date)) &&
        Date.parse(event.start_date) > now &&
        /^[a-zA-Z0-9_-]+$/.test(event.slug) &&
        !!event.image_url,
    )
    .sort((a, b) => Date.parse(a.start_date) - Date.parse(b.start_date))
    .filter((event) => {
      const organizer = (
        event.organizer_name ||
        /\b(intercell|awakenings|teletech|blackworks|verknipt|terminal v)\b/i.exec(
          event.name,
        )?.[1] ||
        event.name
      ).toLowerCase();
      if (seen.has(event.slug) || (organizers.get(organizer) || 0) >= 2)
        return false;
      seen.add(event.slug);
      organizers.set(organizer, (organizers.get(organizer) || 0) + 1);
      return true;
    })
    .slice(0, 4);
}

export function storyPath(story: Pick<EditorialCard, "_type" | "slug">) {
  return `/${story._type === "newsArticle" ? "news" : "articles"}/${story.slug.current}`;
}

/** Keep the editor's lead, then rotate two recent news stories and a feature. */
export function selectFeaturedTopStories<T extends {
  _id: string; _type: string; title: string; slug: { current: string };
  publishedAt: string; imageUrl: string;
}>(cards: T[], pinnedSlug?: string, now = Date.now()): T[] {
  const seen = new Set<string>();
  const available = cards.filter(card => {
    const key = `${card._type}:${card.slug?.current}`;
    if (!card.title || !card.imageUrl || /^drafts\.|^versions\./.test(card._id) ||
        !/^[a-zA-Z0-9_-]+$/.test(card.slug?.current || "") || seen.has(key) ||
        !Number.isFinite(Date.parse(card.publishedAt)) || Date.parse(card.publishedAt) > now ||
        expiredEventGuide(card as unknown as EditorialCard, now)) return false;
    seen.add(key);
    return true;
  }).sort((a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt));
  const lead = available.find(card => card.slug.current === pinnedSlug) || available[0];
  if (!lead) return [];
  const others = available.filter(card => card !== lead &&
    now - Date.parse(card.publishedAt) <= (card._type === "newsArticle" ? 30 : 365) * 86400000);
  const news = others.filter(card => card._type === "newsArticle").slice(0, 2);
  const feature = others.find(card => card._type !== "newsArticle");
  return [...new Set([lead, ...news, ...(feature ? [feature] : []), ...others])].slice(0, 4);
}

export function selectEditorialStories(
  cards: EditorialCard[],
  current: { slug: { current: string }; tags?: string[] },
  events: UpcomingEditorialEvent[] = [],
  now = Date.now(),
) {
  const seen = new Set<string>();
  const published = cards
    .filter((card) => {
      const path = storyPath(card);
      if (
        !/^[a-zA-Z0-9_-]+$/.test(card.slug?.current || "") ||
        card.slug.current === current.slug.current ||
        seen.has(path) ||
        !Number.isFinite(Date.parse(card.publishedAt)) ||
        Date.parse(card.publishedAt) > now ||
        expiredEventGuide(card, now) ||
        (card._type === "newsArticle" &&
          now - Date.parse(card.publishedAt) > 120 * 86400000)
      )
        return false;
      seen.add(path);
      return true;
    })
    .sort((a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt));
  const tags = new Set((current.tags || []).map((tag) => tag.toLowerCase()));
  const score = (card: EditorialCard) =>
    (card.tags || []).filter((tag) => tags.has(tag.toLowerCase())).length;
  const related = published
    .filter((card) => score(card) > 0)
    .sort(
      (a, b) =>
        score(b) - score(a) ||
        Date.parse(b.publishedAt) - Date.parse(a.publishedAt),
    );
  const latest = published.filter((card) => card._type === "newsArticle").slice(0, 4);
  const latestPaths = new Set(latest.map(storyPath));
  const secondary = related
    .filter((card) => !latestPaths.has(storyPath(card)))
    .slice(0, 3);
  const keepReading = [
    ...related,
    ...published.filter((card) => card._type === "postArticle"),
    ...published,
  ];
  const unique = new Map(keepReading.map((card) => [storyPath(card), card]));
  return {
    latest,
    secondary,
    upcoming: selectUpcomingEvents(events, now),
    keepReading: [...unique.values()].slice(0, 3),
  };
}
